import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors } from "../../lib/output.js";
import { getFoundryToken } from "../../lib/auth.js";
import { describeBlockRejection, findOnboardingProfile } from "../../lib/onboarding-profile.js";
import { resolveSeedContext, applyProfileToBrains } from "../../lib/company-profile-seed.js";
import { getSignedInUserIdentity } from "../../lib/founder-identity.js";
import { readOnboardingProfile, toOnboardingBlock } from "../../lib/onboarding-profile-store.js";

export class BootstrapSeedProfileCommand extends M8tCommand {
  static paths = [["bootstrap", "seed-profile"]];
  static usage = Command.Usage({
    description: "Seed your advisors' brains with how to reach you, from what you confirmed at `m8t bootstrap profile`.",
    details: [
      "Renders memory/founder.md + memory/company-profile.md (+ their MEMORY.md index lines)",
      "and commits them to the brains this install created, via the GitHub App. Idempotent —",
      "an unchanged profile is a no-op.",
      "",
      "Run `m8t bootstrap profile` first; this is the manual re-run of the seed that",
      "`m8t bootstrap status --watch` already does when the install completes.",
      "",
      "--watch is legacy: it polls for an onboarding conversation from installs made when a",
      "dedicated intake agent was still deployed. It has nothing to wait for otherwise.",
    ].join("\n"),
    examples: [
      ["Seed now (idempotent)", "$0 bootstrap seed-profile"],
    ],
  });

  endpoint = Option.String("--endpoint", { description: "Override the Foundry endpoint (else read from the install status)." });
  brain = Option.String("--brain", { description: "Seed only this one brain repo, instead of both <org>/stacey-brain and <org>/ezra-brain." });
  watch = Option.Boolean("--watch", false, { description: "Poll until the intake completes (or --timeout)." });
  timeout = Option.String("--timeout", { description: "Watch timeout in minutes (default 20)." });
  githubAppCreds = Option.String("--github-app-creds");

  async executeCommand(): Promise<number> {
    const ctx = await resolveSeedContext({
      endpointOverride: typeof this.endpoint === "string" ? this.endpoint : undefined,
      brainOverride: typeof this.brain === "string" ? this.brain : undefined,
      credsPath: typeof this.githubAppCreds === "string" ? this.githubAppCreds : undefined,
    });
    if (!ctx) {
      this.context.stderr.write(`  ${colors.dim("no GitHub App creds (~/.m8t/github-app.json) — no brain to seed; skipping.")}\n`);
      return 0;
    }
    // Same source order as the install path (reactiveSeedOnInstallComplete): the
    // answers the founder confirmed at `m8t bootstrap profile` win, and resolve
    // without a Foundry token. Keeping the order in step matters — a manual re-run
    // that read a different source than the automatic one would "fix" a brain into
    // a state the next install undoes.
    const local = await readOnboardingProfile();
    if (local) {
      await applyProfileToBrains({
        block: toOnboardingBlock(local),
        brainRepos: ctx.brainRepos,
        branch: "main",
        appCreds: ctx.appCreds,
        subscriptionId: ctx.subscriptionId,
        azIdentity: { name: local.founderName, email: local.founderEmail },
        contactsOnly: true,
        advisorCleared: local.advisorCleared === true,
      });
      this.context.stdout.write(
        `${colors.success("✓")} seeded your advisors' brains (${ctx.brainRepos.join(", ")}) with how to reach you.\n`,
      );
      return 0;
    }

    const watch = this.watch === true;
    const rawTimeout = typeof this.timeout === "string" ? this.timeout.trim() : "";
    const parsedTimeout = Number(rawTimeout);
    // Honor an explicit finite value (including 0); fall back to 20 for unset/empty/non-numeric.
    const timeoutMin = rawTimeout !== "" && Number.isFinite(parsedTimeout) ? parsedTimeout : 20;
    const deadline = Date.now() + timeoutMin * 60_000;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    for (;;) {
      const token = await getFoundryToken();
      const { hadIntake, block, rejection } = await findOnboardingProfile({ endpoint: ctx.endpoint, token });
      if (block) {
        const azIdentity = await getSignedInUserIdentity();
        await applyProfileToBrains({
          block,
          brainRepos: ctx.brainRepos,
          branch: "main",
          appCreds: ctx.appCreds,
          subscriptionId: ctx.subscriptionId,
          azIdentity,
        });
        this.context.stdout.write(`${colors.success("✓")} seeded your advisors' brains (${ctx.brainRepos.join(", ")}) from your intake.\n`);
        return 0;
      }
      if (!watch || Date.now() >= deadline) {
        // "no-artifact" is the fallback whenever nothing was found at all — the common
        // mid-conversation state — so it belongs with the plain not-complete-yet wording,
        // not the "found a block" sentence, which would otherwise contradict itself.
        // Every other reason is only ever set when the raw assistant text contains the
        // "m8t_onboarding" marker (the marker gate in findOnboardingProfile), so a block
        // was genuinely attempted for those.
        if (rejection !== null && rejection !== "no-artifact") {
          // Plain retry re-reads the same immutable conversation and reprints the identical
          // reason forever — it can never change the outcome. findOnboardingProfile only ever
          // looks at the newest ezra-intake conversation (see conversations.at(0) there), and
          // the onboarding surface can't start a second one — it resolves to this same
          // conversation every time. The one action that changes what gets read is removing
          // this conversation (delete it in the Azure AI Foundry portal), so a fresh intake
          // can take its place.
          this.context.stderr.write(
            `  ${colors.dim(`found an onboarding block but could not read it — ${describeBlockRejection(rejection)}.`)}\n` +
            `  ${colors.hint("fix:")} delete this onboarding conversation in the Azure AI Foundry portal, then redo the intake and run: m8t bootstrap seed-profile\n`,
          );
          return 3;
        }
        if (!hadIntake) {
          // Nothing collected locally AND no intake conversation. Re-running this
          // command re-reads the same nothing, forever — there is no intake agent left
          // to start a conversation with. The only command that changes this outcome is
          // the one that actually collects the answers. (The same law that made
          // driftRemedy's "regenerate with <command that does not exist>" a defect.)
          this.context.stderr.write(
            `  ${colors.dim("nothing collected yet — your advisors don't know how to reach you.")}\n` +
            `  ${colors.hint("run:")} m8t bootstrap profile\n`,
          );
          return 3;
        }
        this.context.stderr.write(
          `  ${colors.dim("intake not complete yet — no m8t_onboarding block found.")}\n` +
          `  ${colors.hint("retry:")} m8t bootstrap seed-profile\n`,
        );
        return 3;
      }
      this.context.stderr.write(`  ${colors.dim("waiting for the intake to complete…")}\n`);
      await sleep(20_000);
    }
  }
}
